import { DropdownMenu } from '@dittolive/anvil'
import graphql from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import paths from 'paths'
import { useEffect } from 'react'
import { useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks'
import { useNavigate } from 'react-router-dom'

import { RecentOrganizationAppsMenuItem_viewer$key } from './__generated__/RecentOrganizationAppsMenuItem_viewer.graphql'
import { RecentOrganizationAppsMenuItemQuery } from './__generated__/RecentOrganizationAppsMenuItemQuery.graphql'
import { RecentOrganizationAppsRefetchQuery } from './__generated__/RecentOrganizationAppsRefetchQuery.graphql'

const query = graphql`
  query RecentOrganizationAppsMenuItemQuery {
    viewer {
      ...RecentOrganizationAppsMenuItem_viewer
    }
  }
`

const refetchableOrganizations = graphql`
  fragment RecentOrganizationAppsMenuItem_viewer on Viewer
  @refetchable(queryName: "RecentOrganizationAppsRefetchQuery") {
    me {
      # eslint-disable-next-line relay/unused-fields
      id
      recentOrganizationApps {
        # eslint-disable-next-line relay/unused-fields
        id
        slug
        name
        tenant {
          ... on Organization {
            # eslint-disable-next-line relay/unused-fields
            id
            slug
            name
            __typename
          }
        }
      }
    }
  }
`

type Props = {
  /** True if the menu item is open */
  isOpen: boolean
}

// Menu item for accessing the organization apps that a user has access to.
export default function RecentOrganizationAppsMenuItem({ isOpen }: Props) {
  const { databasesText, isRenamed } = useAppToDatabaseRename()

  const { viewer } = useLazyLoadQuery<RecentOrganizationAppsMenuItemQuery>(
    query,
    {},
  )
  const [data, refetch] = useRefetchableFragment<
    RecentOrganizationAppsRefetchQuery,
    RecentOrganizationAppsMenuItem_viewer$key
  >(refetchableOrganizations, viewer)

  useEffect(() => {
    if (isOpen) {
      refetch({}, { fetchPolicy: 'store-and-network' })
    }
  }, [refetch, isOpen])

  const navigate = useNavigate()

  const recentApps = data.me?.recentOrganizationApps || []
  if (!recentApps.length) {
    return <></>
  }

  return (
    <>
      <DropdownMenu.Label>Recently visited {databasesText}</DropdownMenu.Label>

      {recentApps.map((recentApp, index) => {
        const orgSlug = (recentApp.tenant as { slug: string }).slug || ''
        const orgName = (recentApp.tenant as { name: string }).name || ''

        const redirectTo = paths.app({
          organizationSlug: orgSlug,
          appSlug: recentApp.slug,
          isRenamed,
        })

        return (
          <DropdownMenu.Item key={index} onSelect={() => navigate(redirectTo)}>
            {orgName ? `${orgName} / ${recentApp.name}` : recentApp.name}
          </DropdownMenu.Item>
        )
      })}
    </>
  )
}
